import { api } from '../../api';

export const CONTINUE_PLAYING_HASHTAG = 'continue-playing';

export const NAME_MAX_LENGTH = 50;
export const HASHTAG_MAX_LENGTH = 50;

const EMOJI_PATTERN = /\p{Extended_Pictographic}/u;

export const PACIFIC_TIME_ZONE = 'America/Los_Angeles';

export interface DiscoverShelfBook {
  uuid: string;
  title: string;
  coverUrl: string | null;
  published: boolean;
}

export interface DiscoverShelfPromotion {
  id: number;
  title: string;
  identifier: string;
  status: string;
}

interface ApiShelf {
  id: number;
  name: string;
  hashtag: string | null;
  sort_order: number;
  start_time: string | null;
  end_time: string | null;
  version: number;
  updated_at: string;
}

interface ApiItem {
  id: number;
  shelf_id: number;
  book_uuid: string;
  position: number;
  start_time: string | null;
  end_time: string | null;
  cover_key: string | null;
  cover_url: string | null;
  promotion_id: number | null;
  promo_identifier: string | null;
  active: number;
  book: DiscoverShelfBook | null;
  promotion: DiscoverShelfPromotion | null;
}

export interface WorkingItem {
  key: string;
  id?: number;
  book_uuid: string;
  start_time: string | null;
  end_time: string | null;
  cover_key: string | null;
  cover_url: string | null;
  promotion_id: number | null;
  promo_identifier: string | null;
  active: number;
  book: DiscoverShelfBook | null;
  promotion: DiscoverShelfPromotion | null;
}

export interface WorkingShelf {
  key: string;
  id?: number;
  version?: number;
  updated_at?: string;
  name: string;
  hashtag: string; // '' when the shelf has none
  start_time: string | null;
  end_time: string | null;
  items: WorkingItem[];
}

export interface DiscoverShelvesSaveDiff {
  shelf_versions: Record<number, number>;
  shelves: object[];
  items: object[];
  deleted_item_ids: number[];
  deleted_shelf_ids: number[];
}

let keyCounter = 0;

export const newKey = () => {
  keyCounter += 1;
  return `new-${keyCounter}`;
};

export function toWorkingCopy(shelves: ApiShelf[], items: ApiItem[]): WorkingShelf[] {
  const itemsByShelf = new Map<number, ApiItem[]>();
  [...items]
    .sort((a, b) => a.position - b.position || a.id - b.id)
    .forEach((item) => {
      itemsByShelf.set(item.shelf_id, [...(itemsByShelf.get(item.shelf_id) || []), item]);
    });

  return [...shelves]
    .sort((a, b) => a.sort_order - b.sort_order || a.id - b.id)
    .map((shelf) => ({
      key: `s${shelf.id}`,
      id: shelf.id,
      version: shelf.version,
      updated_at: shelf.updated_at,
      name: shelf.name,
      hashtag: shelf.hashtag || '',
      start_time: shelf.start_time,
      end_time: shelf.end_time,
      items: (itemsByShelf.get(shelf.id) || []).map((item) => ({
        key: `i${item.id}`,
        id: item.id,
        book_uuid: item.book_uuid,
        start_time: item.start_time,
        end_time: item.end_time,
        cover_key: item.cover_key,
        cover_url: item.cover_url,
        promotion_id: item.promotion_id,
        promo_identifier: item.promo_identifier,
        active: item.active,
        book: item.book,
        promotion: item.promotion,
      })),
    }));
}

// Positions and shelf order come from list order. Only new or changed rows are sent; deletes are
// explicit, because expired items that weren't loaded must never be treated as removed.
export function buildSaveDiff(original: WorkingShelf[], working: WorkingShelf[]): DiscoverShelvesSaveDiff {
  const originalShelves = new Map<number, { shelf: WorkingShelf; index: number }>();
  const originalItems = new Map<number, { item: WorkingItem; position: number }>();
  original.forEach((shelf, index) => {
    originalShelves.set(shelf.id as number, { shelf, index });
    shelf.items.forEach((item, itemIndex) => originalItems.set(item.id as number, { item, position: itemIndex + 1 }));
  });

  const shelves: object[] = [];
  const items: object[] = [];
  const keptShelfIds = new Set<number>();
  const keptItemIds = new Set<number>();

  working.forEach((shelf, index) => {
    const sortOrder = index + 1;
    const before = shelf.id ? originalShelves.get(shelf.id) : undefined;
    if (shelf.id) {
      keptShelfIds.add(shelf.id);
    }
    const shelfChanged = !before
      || before.index !== index
      || before.shelf.name !== shelf.name
      || before.shelf.hashtag !== shelf.hashtag
      || before.shelf.start_time !== shelf.start_time
      || before.shelf.end_time !== shelf.end_time;
    if (shelfChanged) {
      shelves.push({
        id: shelf.id,
        ref: shelf.id ? undefined : shelf.key,
        name: shelf.name,
        hashtag: shelf.hashtag,
        sort_order: sortOrder,
        start_time: shelf.start_time,
        end_time: shelf.end_time,
      });
    }

    shelf.items.forEach((item, itemIndex) => {
      const position = itemIndex + 1;
      const itemBefore = item.id ? originalItems.get(item.id) : undefined;
      if (item.id) {
        keptItemIds.add(item.id);
      }
      const itemChanged = !itemBefore
        || itemBefore.position !== position
        || itemBefore.item.book_uuid !== item.book_uuid
        || itemBefore.item.cover_key !== item.cover_key
        || itemBefore.item.start_time !== item.start_time
        || itemBefore.item.end_time !== item.end_time;
      if (itemChanged) {
        items.push({
          id: item.id,
          shelf_id: shelf.id,
          shelf_ref: shelf.id ? undefined : shelf.key,
          book_uuid: item.book_uuid,
          position,
          start_time: item.start_time,
          end_time: item.end_time,
          cover_key: item.cover_key,
          promotion_id: item.id ? undefined : item.promotion_id,
        });
      }
    });
  });

  const deletedShelfIds = original.filter((shelf) => !keptShelfIds.has(shelf.id as number)).map((shelf) => shelf.id as number);
  const deletedShelves = new Set(deletedShelfIds);
  const deletedItemIds: number[] = [];
  original.forEach((shelf) => {
    if (deletedShelves.has(shelf.id as number)) {
      return;
    }
    shelf.items.forEach((item) => {
      if (!keptItemIds.has(item.id as number)) {
        deletedItemIds.push(item.id as number);
      }
    });
  });

  const shelfVersions: Record<number, number> = {};
  original.forEach((shelf) => {
    shelfVersions[shelf.id as number] = shelf.version as number;
  });

  return {
    shelf_versions: shelfVersions,
    shelves,
    items,
    deleted_item_ids: deletedItemIds,
    deleted_shelf_ids: deletedShelfIds,
  };
}

export const isDiffEmpty = (diff: DiscoverShelvesSaveDiff) => !diff.shelves.length
  && !diff.items.length
  && !diff.deleted_item_ids.length
  && !diff.deleted_shelf_ids.length;

// ------------------------------------------------------------------
// Status, validation, preview

export type ScheduleStatus = 'live' | 'scheduled' | 'expired' | 'inactive';

const toMs = (value: string | null) => (value ? new Date(value).getTime() : null);

export function scheduleStatus(
  entry: { start_time: string | null; end_time: string | null; active?: number; promotion_id?: number | null },
  nowMs = Date.now(),
): ScheduleStatus {
  if (entry.active === 0 || (entry.promotion_id && (!entry.start_time || !entry.end_time))) {
    return 'inactive';
  }
  const end = toMs(entry.end_time);
  const start = toMs(entry.start_time);
  if (end != null && end <= nowMs) {
    return 'expired';
  }
  if (start != null && start > nowMs) {
    return 'scheduled';
  }
  return 'live';
}

export function inactiveReason(item: WorkingItem) {
  if (item.promotion_id && (!item.start_time || !item.end_time)) {
    return 'Promotion has no start/end dates';
  }
  if (item.promotion?.status === 'disabled') {
    return 'Promotion is disabled';
  }
  return 'Promotion is disabled or archived';
}

// Both or neither, end after start. Returns an error message or null.
export function validateDatePair(startTime: string | null, endTime: string | null) {
  if (Boolean(startTime) !== Boolean(endTime)) {
    return 'Set both start and end, or neither';
  }
  if (startTime && endTime && new Date(endTime) <= new Date(startTime)) {
    return 'End must be after start';
  }
  return null;
}

const isSameText = (a: string, b: string) => a.trim().toLocaleLowerCase() === b.trim().toLocaleLowerCase();

// Required; any characters; unique ignoring case.
export function validateName(name: string, shelves: WorkingShelf[], shelfKey: string | null) {
  const { length } = Array.from(name.trim());
  if (length < 1 || length > NAME_MAX_LENGTH) {
    return `Name must be 1-${NAME_MAX_LENGTH} characters`;
  }
  if (shelves.some((shelf) => shelf.key !== shelfKey && isSameText(shelf.name, name))) {
    return 'A shelf with that name already exists';
  }
  return null;
}

// Optional; no spaces or emojis; unique ignoring case when set.
export function validateHashtag(hashtag: string, shelves: WorkingShelf[], shelfKey: string | null) {
  const trimmed = hashtag.trim();
  if (!trimmed) {
    return null;
  }
  if (Array.from(trimmed).length > HASHTAG_MAX_LENGTH) {
    return `Hashtag must be at most ${HASHTAG_MAX_LENGTH} characters`;
  }
  if (/\s/.test(trimmed) || EMOJI_PATTERN.test(trimmed)) {
    return 'Hashtag can\'t contain spaces or emojis';
  }
  if (shelves.some((shelf) => shelf.key !== shelfKey && shelf.hashtag.trim() && isSameText(shelf.hashtag, trimmed))) {
    return 'A shelf with that hashtag already exists';
  }
  return null;
}

const isLiveAt = (startTime: string | null, endTime: string | null, atMs: number) => {
  const start = toMs(startTime);
  const end = toMs(endTime);
  return (start == null || start <= atMs) && (end == null || atMs < end);
};

const isItemLiveAt = (item: WorkingItem, atMs: number) => item.active !== 0
  && !(item.promotion_id && (!item.start_time || !item.end_time))
  && isLiveAt(item.start_time, item.end_time, atMs);

type PlacedItem = { item: WorkingItem; position: number };
const isDated = ({ item }: PlacedItem) => Boolean(item.start_time || item.end_time);

// Dated beats undated, then the earliest start, then the earliest position.
const compareDuplicates = (a: PlacedItem, b: PlacedItem) => {
  if (isDated(a) !== isDated(b)) {
    return isDated(a) ? -1 : 1;
  }
  const startA = toMs(a.item.start_time);
  const startB = toMs(b.item.start_time);
  if (startA !== startB) {
    if (startA == null) return 1;
    if (startB == null) return -1;
    return startA - startB;
  }
  return a.position - b.position;
};

// Which items the app would show at `atMs`, per shelf key (shelves the app wouldn't show are absent).
// Same rule as core-api's utils/discoverShelves.js; keep the two in step.
export function resolvePreview(working: WorkingShelf[], atMs: number): Map<string, string[]> {
  const result = new Map<string, string[]>();
  working.forEach((shelf) => {
    if (!isLiveAt(shelf.start_time, shelf.end_time, atMs)) {
      return;
    }
    const winners = new Map<string, PlacedItem>();
    shelf.items
      .map((item, position) => ({ item, position }))
      .filter(({ item }) => isItemLiveAt(item, atMs))
      .forEach((placed) => {
        const current = winners.get(placed.item.book_uuid);
        if (!current || compareDuplicates(placed, current) < 0) {
          winners.set(placed.item.book_uuid, placed);
        }
      });
    const shown = [...winners.values()].sort((a, b) => a.position - b.position).map(({ item }) => item.key);
    if (shown.length || shelf.hashtag === CONTINUE_PLAYING_HASHTAG) {
      result.set(shelf.key, shown);
    }
  });
  return result;
}

export function duplicateBooks(shelf: WorkingShelf) {
  const counts = new Map<string, number>();
  shelf.items.forEach((item) => counts.set(item.book_uuid, (counts.get(item.book_uuid) || 0) + 1));
  return new Set([...counts.entries()].filter(([, count]) => count > 1).map(([uuid]) => uuid));
}

export function apiErrorMessage(err: any, fallback: string, shelves: WorkingShelf[] = []) {
  const data = err?.response?.data;
  if (data?.conflicting_shelf_ids?.length) {
    const names = data.conflicting_shelf_ids
      .map((id: number) => shelves.find((shelf) => shelf.id === id)?.name || `#${id}`)
      .join(', ');
    return `These shelves were changed by someone else since you loaded them: ${names}. Reload to get the latest version.`;
  }
  return data?.error || fallback;
}

// ------------------------------------------------------------------
// API

export const fetchDiscoverShelves = () => api
  .get('/v1/tools/discover-shelves')
  .then((response) => toWorkingCopy(response.data.shelves, response.data.items));

export const saveDiscoverShelves = (diff: DiscoverShelvesSaveDiff) => api.put('/v1/tools/discover-shelves', diff);

export const fetchPromotionDiscoverShelves = (promotionId: number) => api
  .get(`/v1/tools/promotions/${promotionId}/discover-shelves`)
  .then((response) => toWorkingCopy(response.data.shelves, response.data.items));

export const savePromotionDiscoverShelves = (promotionId: number, diff: DiscoverShelvesSaveDiff) => api
  .put(`/v1/tools/promotions/${promotionId}/discover-shelves`, diff);

export const uploadCover = (blob: Blob) => {
  const formData = new FormData();
  formData.append('image', blob, 'cover.jpg');
  return api.post('/v1/tools/discover-shelves/covers', formData)
    .then((response) => ({ key: response.data.key as string, url: response.data.url as string }));
};

export const fetchPreviousCovers = (bookUuid: string) => api
  .get('/v1/tools/discover-shelves/covers', { params: { bookUuid } })
  .then((response) => response.data.covers as { key: string; url: string }[]);

export const searchBooks = (q: string, page: number, signal?: AbortSignal) => api
  .get('/v1/tools/discover-shelves/books/search', { params: { q, page }, signal })
  .then((response) => ({ books: response.data.books as DiscoverShelfBook[], hasMore: Boolean(response.data.hasMore) }));
